import React, {
	useState,
	useRef,
	type KeyboardEvent,
	type ClipboardEvent,
	useEffect,
} from 'react'

import {
	Container,
	InputEmail,
	InputField,
	SubTitle,
	Title,
	Wrapper,
} from './VerificationCode.styles'
import { DeleteAccountProps } from 'Views/DeleteAccount/DeleteAccount.types'
import { Button } from '@welbe/web-design-system'

export interface VerificationCodeInputProps {
	length?: number
	title?: string
	payload?: DeleteAccountProps
	onResend?: () => void
	onChange: (payload: DeleteAccountProps) => void
}

export const VerificationCodeInput = ({
	length = 6,
	payload,
	onResend,
	onChange,
}: VerificationCodeInputProps) => {
	const [code, setCode] = useState<string[]>(Array(length).fill(''))
	const inputRefs = useRef<(HTMLInputElement | null)[]>([])

	useEffect(() => {
		inputRefs.current = inputRefs.current.slice(0, length)
	}, [length])

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number,
	) => {
		const value = e.target.value

		if (value.length > 1) return

		const newCode = [...code]
		newCode[index] = value
		setCode(newCode)

		if (value !== '' && index < length - 1) {
			inputRefs.current[index + 1]?.focus()
		}
	}

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
		if (e.key === 'Backspace') {
			if (code[index] === '' && index > 0) {
				inputRefs.current[index - 1]?.focus()
			} else {
				const newCode = [...code]
				newCode[index] = ''
				setCode(newCode)
			}
		}

		if (e.key === 'ArrowLeft' && index > 0) {
			inputRefs.current[index - 1]?.focus()
		}

		if (e.key === 'ArrowRight' && index < length - 1) {
			inputRefs.current[index + 1]?.focus()
		}
	}

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault()
		const pastedData = e.clipboardData.getData('text/plain').trim()

		if (pastedData.length <= length && /^\d+$/.test(pastedData)) {
			const newCode = [...code]

			for (let i = 0; i < pastedData.length; i++) {
				newCode[i] = pastedData[i]
			}

			setCode(newCode)

			const nextEmptyIndex = newCode.findIndex(digit => digit === '')
			const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex
			inputRefs.current[focusIndex]?.focus()
		}
	}

	useEffect(() => {
		if (code.every(digit => digit)) {
			onChange?.({
				...payload,
				code: code.join(''),
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [code])

	return (
		<Wrapper>
			<Title>Escribe el código que enviamos a</Title>
			<InputEmail value={payload?.email || payload?.phone} readOnly />
			<SubTitle>
				No olvides revisar en todas las bandejas incluyendo{' '}
				<strong>SPAM</strong>
			</SubTitle>
			<Container>
				{Array.from({ length }).map((_, index) => (
					<InputField
						key={index}
						type="text"
						inputMode="numeric"
						maxLength={1}
						pattern="[0-9]*"
						autoComplete="one-time-code"
						value={code[index]}
						onChange={e => handleChange(e, index)}
						onKeyDown={e => handleKeyDown(e, index)}
						onPaste={handlePaste}
						ref={el => (inputRefs.current[index] = el)}
						autoFocus={index === 0}
					/>
				))}
			</Container>
			<Button
				className="resend-button"
				variant="label"
				width="fit-content"
				onClick={() => onResend?.()}
			>
				Reenviar código
			</Button>
		</Wrapper>
	)
}
